import React from 'react';
import { Hourglass } from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';
import {
    Appointment, currentSlot, endTimeOf, formatDay, formatMoney, interventionLabel, needsGarageAnswer, vehicleLabel
} from './appointmentUtils';

interface Props {
    appointments: Appointment[];     // demandes ouvertes (hors agenda de la semaine)
    onOpen: (a: Appointment) => void;
    onQuickAccept: (a: Appointment) => void;
    onOtherSlot: (a: Appointment) => void;
}

const timeAgo = (iso: string | undefined, lang: string): string => {
    if (!iso) return '';
    const diffMin = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
    const rtf = new Intl.RelativeTimeFormat(lang === 'ar' ? 'ar' : lang === 'en' ? 'en' : 'fr', { numeric: 'auto' });
    if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute');
    if (Math.abs(diffMin) < 60 * 24) return rtf.format(Math.round(diffMin / 60), 'hour');
    return rtf.format(Math.round(diffMin / 1440), 'day');
};

// Colonne de droite de l'agenda : « RDV à valider » et rendez-vous en attente du client
export const AgendaSidebar: React.FC<Props> = ({ appointments, onOpen, onQuickAccept, onOtherSlot }) => {
    const { t, currentLanguage, isRTL } = useLanguage();
    const toAnswer = appointments.filter(needsGarageAnswer);
    const waiting = appointments.filter(a => a.awaiting === 'CLIENT');

    const slotLine = (a: Appointment) => {
        const s = currentSlot(a);
        const mode = a.serviceMode === 'WAIT' ? t('appointments.serviceMode.waitShort') : t('appointments.serviceMode.dropShort');
        return `${formatDay(s.date, currentLanguage, { weekday: 'short', day: 'numeric', month: 'short' })} · ${s.timeSlot} – ${endTimeOf(s)} · ${mode}`;
    };

    return (
        <aside className="space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
            <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-gray-900">{t('appointments.requests.toValidate')}</h3>
                    {toAnswer.length > 0 && <span className="min-w-[1.5rem] text-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold">{toAnswer.length}</span>}
                </div>
                {toAnswer.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4 text-center">{t('appointments.requests.emptyText')}</p>
                ) : (
                    <div className="space-y-3">
                        {toAnswer.map(a => (
                            <div key={a._id} className="rounded-xl border border-amber-300 bg-amber-50/60 p-3">
                                <button onClick={() => onOpen(a)} className="w-full text-start">
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="font-bold text-gray-900 text-sm">{a.clientName}{vehicleLabel(a) ? ` · ${vehicleLabel(a)}` : ''}</p>
                                        {a.quoteSnapshot?.totalTTC != null && <span className="font-bold text-green-700 text-sm whitespace-nowrap">{formatMoney(a.quoteSnapshot.totalTTC, currentLanguage)}</span>}
                                    </div>
                                    <p className="text-xs text-gray-600 mt-1">{slotLine(a)}</p>
                                    <p className="text-xs text-gray-500 mt-1">{interventionLabel(a, t(`appointments.type.${a.type}`, { defaultValue: a.type }))} · {t('appointments.requests.receivedAgo', { time: timeAgo(a.createdAt, currentLanguage) })}</p>
                                </button>
                                <div className="mt-3 flex gap-2">
                                    <button onClick={() => onQuickAccept(a)} className="flex-1 px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700">{t('appointments.actions.confirm')}</button>
                                    <button onClick={() => onOtherSlot(a)} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm font-semibold hover:bg-gray-50">{t('appointments.actions.otherSlot')}</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {waiting.length > 0 && (
                <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2"><Hourglass size={16} className="text-amber-500" />{t('appointments.requests.waitingClient')}</h3>
                    <div className="space-y-2">
                        {waiting.map(a => (
                            <button key={a._id} onClick={() => onOpen(a)} className="w-full text-start rounded-xl border border-dashed border-amber-400 px-3 py-2 hover:bg-amber-50/50">
                                <p className="text-sm font-semibold text-gray-900">{a.clientName}{vehicleLabel(a) ? ` · ${vehicleLabel(a)}` : ''}</p>
                                <p className="text-xs text-gray-600 mt-0.5">{slotLine(a)}</p>
                            </button>
                        ))}
                    </div>
                </section>
            )}
        </aside>
    );
};
